/**
 * PublicTranscriptionService - Deepgram Nova-3 streaming transcription for
 * the PublicTranscription page.
 *
 * Mirrors the public API of PublicRecordingService (start/pause/resume/stop +
 * onAudioLevelChange) so the UI components can be reused without changes.
 *
 * What replaces what:
 *   - MediaRecorder + audioChunks[]  →  WebSocket + transcriptChunks[]
 *   - WAV conversion + Supabase upload  →  in-memory transcript accumulation
 *   - Audio-only output  →  text output + command-detection callbacks
 *
 * On every `is_final` Deepgram result we:
 *   1. Append the text to the in-memory transcript buffer
 *   2. Run deterministic command detection
 *   3. Emit each detected command to subscribers (the page hooks fire a
 *      `commandDetected` postMessage to the parent iframe owner)
 */

import { supabaseStorageService } from './supabase-storage-service';
import {
  detectCommands,
  getKeyterms,
  stripVoiceCommands,
  type DetectedCommand,
} from './transcription-commands';

function normalizeSegmentForBuffering(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const BASE_PARAMS: Record<string, string> = {
  model: 'nova-3',
  encoding: 'linear16',
  sample_rate: '16000',
  channels: '1',
  interim_results: 'true',
  // smart_format OFF: with the "access" wake word, Deepgram's post-processor
  // routinely rewrites the lowercase short token to "X S" / "XCESS" /
  // "X.S.", which breaks the START matcher. The regex carries those
  // mangled spellings as fallbacks (see START_TRIGGER_VARIANTS), but
  // keeping smart_format off suppresses the rewrites at the source and
  // means most segments arrive as the canonical "access". Numbers /
  // dates are still rendered (numerals=true below).
  smart_format: 'false',
  punctuate: 'true',
  numerals: 'true',
  // endpointing 350ms: fast enough that command detection feels live,
  // long enough that "access <section>" usually arrives in one segment.
  endpointing: '350',
  language: 'en-US',
  filler_words: 'false',
};

const TARGET_SAMPLE_RATE = 16000;
const KEEPALIVE_INTERVAL_MS = 5000;

function buildDeepgramUrl(keyterms: string[]): string {
  const params = new URLSearchParams(BASE_PARAMS);
  for (const term of keyterms) {
    if (term) params.append('keyterm', term);
  }
  return `wss://api.deepgram.com/v1/listen?${params.toString()}`;
}

export interface TranscriptSegment {
  text: string;
  receivedAt: number;
}

export interface CommandEvent extends DetectedCommand {
  /** The full final transcript line the command appeared in. */
  transcriptSnippet: string;
  detectedAt: number;
}

export interface TranscriptionResult {
  /** All final segments joined with spaces. */
  fullTranscript: string;
  segments: TranscriptSegment[];
  commands: CommandEvent[];
  durationSeconds: number;
}

/**
 * Emitted when a STOP command finalizes the currently active section, OR
 * when a START command implicitly closes a previously-open section that the
 * clinician never explicitly stopped (status='abandoned').
 *
 * The `transcription` string is the per-section text the workflow engine
 * will receive in the `transcription` field of `/api/scribe/transcribe`.
 *
 * `audioUrl` is the signed Supabase URL for the per-section audio recorded
 * in parallel with the Deepgram stream (same path the original
 * PublicRecording flow used). Empty string if the audio couldn't be
 * uploaded — the transcript is still sent so the section is never lost.
 */
export interface SectionCompletedEvent {
  /** Canonical sectionTag for the gateway / EyePegasus. */
  sectionTag: string;
  /** Natural-language keyword the clinician spoke. */
  keyword: string;
  /** Concatenated section transcript (final segments only). */
  transcription: string;
  /** Signed Supabase URL of the per-section audio file (WAV). */
  audioUrl: string;
  /** Individual final segments captured during the section. */
  segments: TranscriptSegment[];
  /** ms epoch when the START command fired. */
  startedAt: number;
  /** ms epoch when the section closed. */
  endedAt: number;
  /** Active duration in seconds (endedAt − startedAt). */
  durationSeconds: number;
  /** 'completed' = explicit STOP; 'abandoned' = closed by next START. */
  status: 'completed' | 'abandoned';
}

/**
 * Context the page passes to the service so per-section audio can be
 * uploaded to Supabase Storage at the right path. Mirrors the config
 * shape that the original PublicRecordingService.uploadRecording uses,
 * minus the per-call sectionTag (which we already know from the active
 * voice-command section).
 */
export interface TranscriptionUploadContext {
  locationId: string;
  patientId: string;
  appointmentId: string;
  scribeJobId: string;
}

type AudioLevelListener = (level: number) => void;
type CommandListener = (command: CommandEvent) => void;
type TranscriptListener = (segment: TranscriptSegment) => void;
type SectionCompletedListener = (event: SectionCompletedEvent) => void;
type RecordingStartedListener = () => void;

export class PublicTranscriptionService {
  private apiKey: string;

  // Connection / audio state
  private socket: WebSocket | null = null;
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private keepAliveTimer: ReturnType<typeof setInterval> | null = null;

  private isRecording = false;
  private isPaused = false;
  private recordingStartTime = 0;
  private totalPausedDuration = 0;
  private lastPauseTime = 0;

  // Waveform support (reuses the same AnalyserNode pattern as PublicRecordingService)
  private analyser: AnalyserNode | null = null;
  private timeDomainArray: Float32Array | null = null;
  private audioLevelAnimationFrame: number | null = null;
  private audioLevelListeners: AudioLevelListener[] = [];

  // In-memory transcript accumulation
  private transcriptSegments: TranscriptSegment[] = [];
  private detectedCommands: CommandEvent[] = [];
  private commandListeners: CommandListener[] = [];
  private transcriptListeners: TranscriptListener[] = [];

  // Section-completed event subscribers (one per page that wants to
  // upload to the gateway when a Generate click fires).
  private sectionListeners: SectionCompletedListener[] = [];

  // Fires exactly once per session — at the moment the first PCM
  // frame is successfully sent to Deepgram (mic permission granted,
  // WebSocket open, audio flowing). The page bridges this to a
  // postMessage so the host EHR can enable its Generate buttons only
  // when a click would actually capture something. Reset on each
  // start (resets `hasFiredRecordingStarted` below) so re-opening
  // the iframe re-emits the signal.
  private recordingStartedListeners: RecordingStartedListener[] = [];
  private hasFiredRecordingStarted = false;

  // Cursor-based section model (replaces the prior voice-START/STOP
  // active-section state machine).
  //
  // Transcription runs continuously the entire time the user is in a
  // voice-scribe session. When the user clicks "Generate" on a test
  // card, we ship every transcript segment captured since the previous
  // Generate (or since recording start, for the first click) as that
  // test's payload, then advance both cursors. Audio for that range is
  // sliced from the always-on PCM buffer by timestamp.
  //
  // The first Generate captures everything from the start of
  // recording. Subsequent Generates capture only the new audio /
  // transcript that arrived in between — n8n handles empty payloads
  // gracefully so double-clicks aren't an error.
  private uploadContext: TranscriptionUploadContext | null = null;
  private sectionCursorIndex = 0; // next un-consumed transcriptSegments index
  private sectionCursorTime = 0;  // wall-clock floor (ms epoch) for next PCM slice

  // Independent cursor for the GLOBAL ENCOUNTER-LEVEL workflow ("Generate
  // Document" button next to the Access chip). The clinician records one
  // continuous stream covering the whole encounter, then clicks Generate
  // Document — that slices [globalCursor..end] and ships to n8n's global
  // workflow which fans out per-section extraction.
  //
  // Kept ORTHOGONAL to sectionCursor on purpose:
  //   - per-section Generate / "access document" advances sectionCursor
  //     ONLY (global Generate still sees the same content).
  //   - global Generate advances globalCursor ONLY (per-section operations
  //     keep working in parallel).
  //   - navigate command advances sectionCursor ONLY (the global buffer
  //     spans the whole encounter regardless of voice navigation).
  private globalCursorIndex = 0;
  private globalCursorTime = 0;

  // Always-on PCM buffer. Every frame the ScriptProcessor sends to
  // Deepgram is also pushed here with its wall-clock timestamp. On
  // Generate we slice by `t >= sectionCursorTime` to find the audio
  // belonging to the section. Chunks older than the cursor are dropped
  // after each Generate to bound memory in long sessions.
  private pcmChunks: Array<{ samples: Int16Array; t: number }> = [];

  // Pipeline diagnostics — every audio frame the ScriptProcessor delivers
  // increments these. The heartbeat below logs deltas so we can see at a
  // glance whether the audio pipeline is alive, throttled, or dead.
  private audioFrameCount = 0;
  private sentFrameCount = 0;
  private skippedFrameCount = 0;
  private sectionFrameCount = 0;
  private lastAudioFrameAt = 0;
  private lastHeartbeatFrames = 0;
  private lastHeartbeatSent = 0;

  constructor(apiKey: string) {
    if (!apiKey) {
      throw new Error('Deepgram API key is required');
    }
    this.apiKey = apiKey;
  }

  /**
   * Provide the IDs needed to upload per-section audio to Supabase Storage.
   * Should be called before `startRecording()` so the very first STOP can
   * upload. Without this, sections still finalize but `audioUrl` ends up
   * empty (gateway gets transcript only).
   */
  setUploadContext(context: TranscriptionUploadContext): void {
    this.uploadContext = context;
  }

  /**
   * Acquire the microphone and wire up the audio analysis graph used for the
   * waveform visualization.
   */
  private async initializeStream(): Promise<MediaStream> {
    if (this.stream) return this.stream;
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
        },
      });

      // Attach diagnostic listeners to the MediaStream and its tracks so we
      // can see exactly when (and from where) the OS / browser revokes mic
      // access. If `onended` fires while we still believe we're recording,
      // it means something external killed the track — typically the iframe
      // being destroyed by its parent document, or user-revoked permission.
      this.stream.getAudioTracks().forEach((track, i) => {
        const desc = `track[${i}] label=${track.label} kind=${track.kind}`;
        track.addEventListener('ended', () => {
          console.error(
            `[PublicTranscription] 🛑 MediaStreamTrack ENDED — ${desc}`,
            {
              readyState: track.readyState,
              isRecording: this.isRecording,
              isPaused: this.isPaused,
              audioContextState: this.audioContext?.state,
              wallTime: new Date().toISOString(),
            }
          );
        });
        track.addEventListener('mute', () => {
          console.warn(
            `[PublicTranscription] 🔇 MediaStreamTrack MUTED — ${desc}`
          );
        });
        track.addEventListener('unmute', () => {
          console.log(
            `[PublicTranscription] 🔊 MediaStreamTrack UNMUTED — ${desc}`
          );
        });
      });
      this.stream.addEventListener('removetrack', (e) => {
        console.error(
          '[PublicTranscription] 🛑 MediaStream removetrack',
          (e as any).track?.label
        );
      });

      this.audioContext = new AudioContext();
      this.audioContext.addEventListener('statechange', () => {
        const state = this.audioContext?.state;
        console.warn(
          '[PublicTranscription] 🔊 AudioContext state changed →',
          state,
          { isRecording: this.isRecording, isPaused: this.isPaused }
        );
        // If the browser auto-suspended the context (iframe focus loss,
        // navigation, etc.) while we're recording, try to recover.
        if (state === 'suspended' && this.isRecording) {
          this.audioContext?.resume().catch(() => {
            /* noop */
          });
        }
      });
      this.source = this.audioContext.createMediaStreamSource(this.stream);
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      this.analyser.smoothingTimeConstant = 0.8;
      this.timeDomainArray = new Float32Array(this.analyser.frequencyBinCount);
      this.source.connect(this.analyser);
    } catch (error) {
      console.error('❌ Failed to get microphone access:', error);
      throw new Error('Microphone access denied or unavailable');
    }
    return this.stream;
  }

  /**
   * Open the Deepgram WebSocket and start streaming PCM16.
   */
  async startRecording(): Promise<void> {
    if (this.isRecording) {
      console.warn('Transcription is already in progress');
      return;
    }

    const stream = await this.initializeStream();

    // Reset session state
    this.transcriptSegments = [];
    this.detectedCommands = [];
    this.pcmChunks = [];
    // Reset the once-only "first audio frame sent" guard so a fresh
    // session re-emits the recordingStarted signal.
    this.hasFiredRecordingStarted = false;
    this.recordingStartTime = Date.now();
    // Cursor floor for the FIRST Generate: every audio frame and every
    // transcript segment captured from this moment on belongs to
    // whichever section the user clicks Generate on first.
    this.sectionCursorIndex = 0;
    this.sectionCursorTime = this.recordingStartTime;
    // Global Generate (encounter-level workflow) shares the same starting
    // point as the per-section cursor for the FIRST click — both capture
    // everything from recording start. After that the two cursors run
    // independently.
    this.globalCursorIndex = 0;
    this.globalCursorTime = this.recordingStartTime;
    this.totalPausedDuration = 0;
    this.lastPauseTime = 0;

    await this.openSocket();
    this.attachAudioPipeline(stream);

    this.isRecording = true;
    this.isPaused = false;
    this.startAudioLevelDetection();

    // Defensive: in an iframe loaded without a direct user gesture, the
    // AudioContext may have been created in 'suspended' state and won't
    // fire onaudioprocess until explicitly resumed.
    if (this.audioContext && this.audioContext.state === 'suspended') {
      console.warn(
        '[PublicTranscription] AudioContext is suspended on start — resuming'
      );
      this.audioContext.resume().catch((err) => {
        console.error(
          '[PublicTranscription] AudioContext resume failed:',
          err
        );
      });
    }
    console.log('✅ Started public transcription', {
      audioContextState: this.audioContext?.state,
      sampleRate: this.audioContext?.sampleRate,
      streamTracks: this.stream?.getAudioTracks().map((t) => ({
        label: t.label,
        enabled: t.enabled,
        muted: t.muted,
        readyState: t.readyState,
      })),
    });
  }

  private openSocket(): Promise<void> {
    return new Promise((resolve, reject) => {
      const url = buildDeepgramUrl(getKeyterms());
      const socket = new WebSocket(url, ['token', this.apiKey]);
      socket.binaryType = 'arraybuffer';

      const onOpen = () => {
        socket.removeEventListener('open', onOpen);
        socket.removeEventListener('error', onErrorBeforeOpen);
        this.socket = socket;
        this.keepAliveTimer = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'KeepAlive' }));
            // Frame-rate heartbeat every 8s — makes it immediately obvious
            // in DevTools whether the audio pipeline is still alive,
            // whether sent frames are growing, and whether the section
            // buffer is accumulating while a section is open.
            const framesDelta =
              this.audioFrameCount - this.lastHeartbeatFrames;
            const sentDelta = this.sentFrameCount - this.lastHeartbeatSent;
            const sinceLastFrameMs =
              this.lastAudioFrameAt > 0
                ? Math.round(performance.now() - this.lastAudioFrameAt)
                : -1;
            const audioStalled = framesDelta === 0 && this.isRecording;
            const logFn = audioStalled ? console.error : console.log;
            logFn(
              '[PublicTranscription] ❤️ heartbeat',
              {
                socketState: socket.readyState,
                audioContextState: this.audioContext?.state || 'null',
                isRecording: this.isRecording,
                isPaused: this.isPaused,
                framesLast8s: framesDelta,
                sentLast8s: sentDelta,
                totalSegments: this.transcriptSegments.length,
                pcmChunksBuffered: this.pcmChunks.length,
                sectionCursorIndex: this.sectionCursorIndex,
                msSinceLastFrame: sinceLastFrameMs,
                streamLive: this.stream?.getAudioTracks()[0]?.readyState,
                ...(audioStalled
                  ? { ALERT: 'NO AUDIO FRAMES IN LAST 8 SECONDS' }
                  : {}),
              }
            );
            this.lastHeartbeatFrames = this.audioFrameCount;
            this.lastHeartbeatSent = this.sentFrameCount;

            // If audio stalled but we believe we're recording, try to
            // recover by resuming the AudioContext.
            if (audioStalled && this.audioContext?.state === 'suspended') {
              console.warn(
                '[PublicTranscription] Auto-recover: AudioContext was suspended, resuming'
              );
              this.audioContext.resume().catch(() => {
                /* noop */
              });
            }
          }
        }, KEEPALIVE_INTERVAL_MS);
        resolve();
      };

      const onErrorBeforeOpen = () => {
        socket.removeEventListener('open', onOpen);
        socket.removeEventListener('error', onErrorBeforeOpen);
        reject(new Error('Failed to open Deepgram WebSocket'));
      };

      socket.addEventListener('open', onOpen);
      socket.addEventListener('error', onErrorBeforeOpen);
      socket.addEventListener('message', this.handleSocketMessage);
      socket.addEventListener('close', this.handleSocketClose);
    });
  }

  private handleSocketMessage = (event: MessageEvent) => {
    let payload: any;
    try {
      payload = JSON.parse(event.data as string);
    } catch {
      return;
    }
    if (payload?.type !== 'Results') return;
    const alt = payload?.channel?.alternatives?.[0];
    const text: string = alt?.transcript || '';
    if (!text || !payload.is_final) return;

    const receivedAt = Date.now();
    const segment: TranscriptSegment = { text, receivedAt };
    this.transcriptSegments.push(segment);
    this.transcriptListeners.forEach((cb) => cb(segment));

    console.log('[PublicTranscription] final segment:', text);

    // Run the matcher against the freshly-final segment. Three command
    // kinds reach this code path:
    //   - 'navigate' — advance the section cursor past this segment so
    //     pre-navigate dictation (typically from an abandoned previous
    //     section) doesn't bleed into the section the user just opened.
    //   - 'document' — strip the command phrase from the trigger
    //     segment's text in place. The upcoming Generate's slice will
    //     then contain dictation only, never the "access document" /
    //     "document <section>" token itself.
    //   - 'speak'    — TTS read-back. No buffer mutation.
    const matches = detectCommands(text);
    let hasNavigate = false;
    let hasDocument = false;
    for (const m of matches) {
      if (m.kind === 'navigate') hasNavigate = true;
      if (m.kind === 'document') hasDocument = true;
      const cmd: CommandEvent = {
        ...m,
        transcriptSnippet: text,
        detectedAt: receivedAt,
      };
      this.detectedCommands.push(cmd);
      this.commandListeners.forEach((cb) => cb(cmd));
    }

    if (hasNavigate) {
      // Drop everything captured BEFORE this navigate from the next
      // Generate's slice. The user's intent: "this section starts now;
      // ignore anything I said before opening it." Deepgram emits
      // finals in audio-time order, so by the time the navigate final
      // landed all prior finals are already in transcriptSegments —
      // moving the cursor to current length excludes them all
      // synchronously, no race with pending in-flight audio.
      this.sectionCursorIndex = this.transcriptSegments.length;
      this.sectionCursorTime = receivedAt;
      this.pcmChunks = this.pcmChunks.filter((c) => c.t >= receivedAt);
      console.log(
        '[PublicTranscription] navigate → cursor advanced to',
        this.sectionCursorIndex,
        `(${this.pcmChunks.length} pcm chunks retained)`
      );
    }
    if (hasDocument) {
      // Clean the trigger segment so the about-to-fire Generate slice
      // excludes the command phrase. Mixed segments ("X Y Z access
      // document") keep their dictation; pure-command segments
      // ("access document") get popped entirely.
      const last = this.transcriptSegments[this.transcriptSegments.length - 1];
      if (last) {
        const cleaned = stripVoiceCommands(last.text);
        if (cleaned === '') {
          this.transcriptSegments.pop();
        } else {
          last.text = cleaned;
        }
      }
    }
  };

  /**
   * Generate-button handler — finalizes whatever transcript + audio
   * has accumulated since the last Generate (or since recording start
   * for the first Generate click), tags it with the supplied section
   * metadata, and emits a SectionCompletedEvent so the page can POST
   * to the gateway.
   *
   * The cursor then advances to "now", so the next Generate captures
   * only audio captured AFTER this click. Empty slices are still
   * emitted — n8n handles them; double-clicks aren't an error.
   */
  async generateSectionForKeyword(sectionTag: string, keyword: string): Promise<void> {
    if (!sectionTag) {
      console.warn(
        '[PublicTranscription] generateSectionForKeyword: missing sectionTag'
      );
      return;
    }
    if (!this.isRecording) {
      console.warn(
        '[PublicTranscription] generateSectionForKeyword: recording not active — ignoring click'
      );
      return;
    }
    // Flush any in-flight finals from Deepgram BEFORE slicing.
    //
    // Why: Deepgram emits "final" segments on detected endpointing
    // (350ms of silence with our config) or at internal sentence
    // boundaries. When the user clicks Generate while still speaking
    // — or within ~500ms of finishing — the trailing audio is still
    // being processed by Deepgram's server. Without flushing, that
    // audio's final segment arrives moments after our slice has
    // already advanced the cursor, so it ends up in the NEXT Generate
    // instead of this one. That was the "right eye made it but left
    // eye didn't" bug.
    //
    // Sending {type:'Finalize'} tells Deepgram to immediately emit
    // any pending audio as a final. We wait for that final to land
    // (or a timeout if there's nothing to flush) plus a small grace
    // period for any followup finals, then proceed with the slice.
    await this.flushPendingDeepgramFinals();

    const endedAt = Date.now();
    const startedAt = this.sectionCursorTime || this.recordingStartTime;

    // Slice transcript segments captured since the last cursor.
    const sliceStart = this.sectionCursorIndex;
    const sliceEnd = this.transcriptSegments.length;
    const sectionSegments = this.transcriptSegments.slice(
      sliceStart,
      sliceEnd
    );
    const rawTranscription = sectionSegments
      .map((s) => s.text)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    // Defence in depth — handleSocketMessage already cleans the
    // document trigger segment AND advances cursor past navigate
    // triggers, but a stray command embedded in a mixed segment (e.g.
    // "the patient says access ophthalmoscopy is fine") would still
    // reach the LLM. Strip every recognised command phrase from the
    // joined slice text before it ships to n8n.
    const transcription = stripVoiceCommands(rawTranscription);

    // Snapshot PCM chunks captured since the last cursor time. Take the
    // snapshot BEFORE advancing the cursor so concurrent audio-frame
    // writes can't accidentally land in the snapshot. We keep
    // sectionCursorTime exclusive on both ends: prior Generate's slice
    // ended at the old cursor, this one starts at it and ends at now.
    const cursorFloor = this.sectionCursorTime;
    const chunkSnapshot = this.pcmChunks.filter((c) => c.t >= cursorFloor);

    // Advance cursors. After this point, new transcript segments + PCM
    // frames are attributed to the NEXT Generate.
    this.sectionCursorIndex = sliceEnd;
    this.sectionCursorTime = endedAt;

    // Bound memory: drop already-uploaded PCM. The snapshot is its own
    // array reference so the upload still has the data.
    this.pcmChunks = this.pcmChunks.filter((c) => c.t >= endedAt);

    const event: SectionCompletedEvent = {
      sectionTag,
      keyword,
      transcription,
      audioUrl: '',
      segments: sectionSegments,
      startedAt,
      endedAt,
      durationSeconds: Math.max(0, Math.round((endedAt - startedAt) / 1000)),
      status: 'completed',
    };
    console.log(
      '[PublicTranscription] generate:',
      keyword,
      '→',
      sectionTag,
      `(${transcription.length} chars, ${event.durationSeconds}s, segments ${sliceStart}-${sliceEnd}, ${chunkSnapshot.length} pcm chunks)`
    );
    this.sectionListeners.forEach((cb) => cb(event));

    // Fire-and-forget the Supabase audio upload for this slice.
    void this.uploadSectionAudioInBackground(
      sectionTag,
      keyword,
      chunkSnapshot,
      endedAt
    );
  }

  /**
   * "Generate Document" handler — encounter-level workflow trigger. Same
   * flush-then-slice pattern as generateSectionForKeyword, but slices
   * against the INDEPENDENT global cursor and emits a sectionCompleted
   * event tagged with sectionTag='global' so the page can route it to
   * the gateway's /api/scribe/global-workflow endpoint (which fans out
   * per-section extraction in n8n).
   *
   * Does NOT touch sectionCursor — the clinician can keep using
   * per-section Generate clicks / "access document" voice commands
   * alongside the global flow.
   */
  async generateGlobalWorkflow(): Promise<void> {
    if (!this.isRecording) {
      console.warn(
        '[PublicTranscription] generateGlobalWorkflow: recording not active — ignoring click'
      );
      return;
    }
    await this.flushPendingDeepgramFinals();

    const endedAt = Date.now();
    const startedAt = this.globalCursorTime || this.recordingStartTime;

    const sliceStart = this.globalCursorIndex;
    const sliceEnd = this.transcriptSegments.length;
    const sectionSegments = this.transcriptSegments.slice(sliceStart, sliceEnd);
    const rawTranscription = sectionSegments
      .map((s) => s.text)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    const transcription = stripVoiceCommands(rawTranscription);

    // Snapshot PCM by global-cursor time, mirroring the per-section flow.
    const cursorFloor = this.globalCursorTime;
    const chunkSnapshot = this.pcmChunks.filter((c) => c.t >= cursorFloor);

    // Advance the global cursor only — per-section state is unaffected.
    this.globalCursorIndex = sliceEnd;
    this.globalCursorTime = endedAt;

    const event: SectionCompletedEvent = {
      sectionTag: 'global',
      keyword: 'global',
      transcription,
      audioUrl: '',
      segments: sectionSegments,
      startedAt,
      endedAt,
      durationSeconds: Math.max(0, Math.round((endedAt - startedAt) / 1000)),
      status: 'completed',
    };
    console.log(
      '[PublicTranscription] generate GLOBAL:',
      `(${transcription.length} chars, ${event.durationSeconds}s, segments ${sliceStart}-${sliceEnd}, ${chunkSnapshot.length} pcm chunks)`
    );
    this.sectionListeners.forEach((cb) => cb(event));

    // Background audio upload uses the same Supabase helper as
    // per-section uploads — tagged with sectionTag='global' so the
    // file path differentiates it from per-section audio.
    void this.uploadSectionAudioInBackground(
      'global',
      'global',
      chunkSnapshot,
      endedAt
    );
  }

  /**
   * Force Deepgram to emit a final for any audio it has buffered, then
   * wait briefly for that final (and any followup finals) to actually
   * arrive in our handleSocketMessage. Used by generateSectionForKeyword
   * to make sure the slice includes the user's full dictation up to
   * the click moment.
   *
   * Strategy:
   *   1. Send {type:'Finalize'} over the WS — Deepgram processes any
   *      pending audio and emits the corresponding final(s).
   *   2. Wait for the NEXT final-segment to land (via a one-shot
   *      transcript listener) OR a hard timeout if there's nothing to
   *      flush (user was silent at click time).
   *   3. After the first final, wait a small grace period — Finalize
   *      can produce multiple finals if there were several sentence
   *      boundaries pending. The grace period lets them all settle
   *      before the caller slices.
   *
   * No-op if the socket isn't open (e.g. we're tearing down).
   */
  private async flushPendingDeepgramFinals(
    maxWaitMs = 1500,
    postFlushGraceMs = 250
  ): Promise<void> {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    try {
      socket.send(JSON.stringify({ type: 'Finalize' }));
    } catch (err) {
      console.warn(
        '[PublicTranscription] Finalize send failed; proceeding with current buffer',
        err
      );
      return;
    }
    await new Promise<void>((resolve) => {
      let settled = false;
      const settleOnce = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        const idx = this.transcriptListeners.indexOf(listener);
        if (idx > -1) this.transcriptListeners.splice(idx, 1);
        resolve();
      };
      // Resolve on the next final segment Deepgram emits — that's
      // typically the one produced by the Finalize we just sent.
      const listener: TranscriptListener = () => settleOnce();
      this.transcriptListeners.push(listener);
      // Safety net: if Deepgram had nothing to flush (silence at
      // click time), no final will arrive. Resolve after maxWaitMs so
      // the Generate flow proceeds rather than hanging forever.
      const timer = window.setTimeout(settleOnce, maxWaitMs);
    });
    // Grace period for any followup finals — handleSocketMessage
    // appends each final to transcriptSegments synchronously, so a
    // brief sleep here is enough for Deepgram to push the rest.
    await new Promise<void>((r) => window.setTimeout(r, postFlushGraceMs));
  }

  private async uploadSectionAudioInBackground(
    sectionTag: string,
    keyword: string,
    chunks: Array<{ samples: Int16Array; t: number }>,
    endedAt: number
  ): Promise<void> {
    if (!chunks || chunks.length === 0) return;
    if (!this.uploadContext) {
      console.warn(
        '[PublicTranscription] No uploadContext — skipping background audio upload for',
        keyword
      );
      return;
    }
    try {
      const totalSamples = chunks.reduce(
        (sum, c) => sum + c.samples.length,
        0
      );
      if (totalSamples === 0) return;
      const combined = new Int16Array(totalSamples);
      let offset = 0;
      for (const c of chunks) {
        combined.set(c.samples, offset);
        offset += c.samples.length;
      }
      const wavBuffer = this.pcm16MonoToWav(combined, TARGET_SAMPLE_RATE);
      const blob = new Blob([wavBuffer], { type: 'audio/wav' });
      const fileName = `${sectionTag}-${endedAt}.wav`;
      const file = new File([blob], fileName, { type: 'audio/wav' });
      const uploadResult = await supabaseStorageService.uploadAudioFile(
        file,
        {
          locationId: this.uploadContext.locationId,
          patientId: this.uploadContext.patientId,
          appointmentId: this.uploadContext.appointmentId,
          scribeJobId: this.uploadContext.scribeJobId,
          sectionTag,
        }
      );
      console.log(
        `[PublicTranscription] background audio upload for ${keyword}:`,
        uploadResult.success
          ? `OK (${uploadResult.signedUrl?.slice(0, 60)}…)`
          : `FAILED — ${uploadResult.error}`
      );
    } catch (err) {
      console.error(
        '[PublicTranscription] background audio upload threw:',
        err
      );
    }
  }

  /** Wrap a 16-bit mono PCM payload in a RIFF/WAVE header. */
  private pcm16MonoToWav(
    samples: Int16Array,
    sampleRate: number
  ): ArrayBuffer {
    const numChannels = 1;
    const bytesPerSample = 2;
    const byteRate = sampleRate * numChannels * bytesPerSample;
    const blockAlign = numChannels * bytesPerSample;
    const dataLength = samples.byteLength;
    const buffer = new ArrayBuffer(44 + dataLength);
    const view = new DataView(buffer);
    const writeString = (off: number, s: string) => {
      for (let i = 0; i < s.length; i++) {
        view.setUint8(off + i, s.charCodeAt(i));
      }
    };
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataLength, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true); // fmt chunk size
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true); // 16-bit
    writeString(36, 'data');
    view.setUint32(40, dataLength, true);
    // Copy little-endian Int16 samples.
    const pcmView = new DataView(buffer, 44);
    for (let i = 0; i < samples.length; i++) {
      pcmView.setInt16(i * 2, samples[i], true);
    }
    return buffer;
  }

  private handleSocketClose = (event: CloseEvent) => {
    // Always log — distinguishing a clean close from an unexpected one is
    // useful when diagnosing "listening stopped" complaints. The expected
    // codes are 1000 (we called close ourselves) and 1005 (no status).
    const expected = event.code === 1000 || event.code === 1005;
    const fn = expected ? console.log : console.warn;
    fn(
      '[PublicTranscription] Deepgram socket closed',
      `code=${event.code}`,
      event.reason || '',
      `(was recording: ${this.isRecording})`
    );
    // If the socket dies while we believe we're still recording, the user
    // is going to see "Listening…" but nothing will happen. Mark the
    // service so the UI can react.
    if (!expected && this.isRecording) {
      this.isRecording = false;
      this.isPaused = false;
    }
  };

  private attachAudioPipeline(stream: MediaStream): void {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }
    const inputRate = this.audioContext.sampleRate;
    if (!this.source) {
      this.source = this.audioContext.createMediaStreamSource(stream);
    }

    // ScriptProcessorNode is deprecated but is the simplest cross-browser path
    // to raw PCM frames. Buffer 4096 ≈ ~85ms at 48kHz which is fine for
    // streaming speech.
    const bufferSize = 4096;
    this.processor = this.audioContext.createScriptProcessor(bufferSize, 1, 1);
    this.processor.onaudioprocess = (event) => {
      // Always count frames — even ones we skip — so the heartbeat can
      // tell us "audio is reaching the processor but we chose not to send"
      // vs "audio frames stopped reaching us entirely".
      this.audioFrameCount++;
      this.lastAudioFrameAt = performance.now();

      if (!this.isRecording || this.isPaused) {
        this.skippedFrameCount++;
        return;
      }
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        this.skippedFrameCount++;
        return;
      }
      const input = event.inputBuffer.getChannelData(0);
      const downsampled = downsampleFloat32(
        input,
        inputRate,
        TARGET_SAMPLE_RATE
      );
      const pcm16 = floatTo16BitPCM(downsampled);
      this.socket.send(pcm16);
      this.sentFrameCount++;
      // First successful send of this session — the only honest
      // signal that mic + WebSocket are both live. Fire once so the
      // host EHR can enable its Generate buttons. Wrapped in
      // try/catch so a misbehaving subscriber can't stall the audio
      // pipeline.
      if (!this.hasFiredRecordingStarted) {
        this.hasFiredRecordingStarted = true;
        for (const cb of this.recordingStartedListeners) {
          try {
            cb();
          } catch (err) {
            console.warn(
              '[PublicTranscription] recordingStarted listener threw',
              err
            );
          }
        }
      }
      // Always mirror the frame into the global PCM buffer with a
      // wall-clock timestamp. On Generate-click we slice by timestamp
      // (chunks at-or-after the last cursor) to find the audio
      // belonging to the section. Single ScriptProcessor sink — no
      // MediaRecorder / AudioContext contention.
      this.pcmChunks.push({
        samples: new Int16Array(pcm16),
        t: Date.now(),
      });
      this.sectionFrameCount++;
    };
    this.source.connect(this.processor);
    this.processor.connect(this.audioContext.destination);
  }

  /**
   * Stop sending audio frames; keep the socket alive via KeepAlive pings so
   * the Deepgram session resumes cleanly.
   */
  async pauseRecording(): Promise<void> {
    if (!this.isRecording || this.isPaused) return;
    this.isPaused = true;
    this.lastPauseTime = Date.now();
    this.stopAudioLevelDetection();
    // No MediaRecorder to pause — the ScriptProcessor checks isPaused on
    // every frame and skips writing to both Deepgram and the section PCM
    // buffer while paused, so audio capture naturally pauses too.
    console.log('⏸️ Paused public transcription');
  }

  async resumeRecording(): Promise<void> {
    if (!this.isRecording || !this.isPaused) return;
    this.isPaused = false;
    if (this.lastPauseTime > 0) {
      this.totalPausedDuration += Date.now() - this.lastPauseTime;
      this.lastPauseTime = 0;
    }
    this.startAudioLevelDetection();
    console.log('▶️ Resumed public transcription');
  }

  /**
   * Close the socket and return the accumulated transcript + detected
   * commands. The caller (handler hook) is responsible for forwarding this
   * to the parent window via postMessage.
   */
  async stopRecording(): Promise<TranscriptionResult> {
    if (!this.isRecording) {
      throw new Error('No active transcription to stop');
    }
    // Flip the flag IMMEDIATELY so a second concurrent call (React strict
    // mode unmount + manual stop, etc.) hits the guard above and returns
    // without re-finalizing the active section. Otherwise we'd upload the
    // same audio + transcription twice.
    this.isRecording = false;

    this.stopAudioLevelDetection();

    // Tell Deepgram we're done sending audio; this flushes any pending finals.
    try {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ type: 'CloseStream' }));
      }
    } catch {
      /* ignore */
    }

    // Give Deepgram a brief window to emit trailing finals before tearing down.
    await new Promise((r) => setTimeout(r, 300));

    const totalElapsed = Date.now() - this.recordingStartTime;
    let pausedDuration = this.totalPausedDuration;
    if (this.isPaused && this.lastPauseTime > 0) {
      pausedDuration += Date.now() - this.lastPauseTime;
    }
    const durationSeconds = Math.round(
      Math.max(0, totalElapsed - pausedDuration) / 1000
    );

    const result: TranscriptionResult = {
      fullTranscript: this.transcriptSegments
        .map((s) => s.text)
        .join(' ')
        .trim(),
      segments: [...this.transcriptSegments],
      commands: [...this.detectedCommands],
      durationSeconds,
    };

    this.cleanup();
    // isRecording was already flipped at the top of this method as a
    // dedup guard; just clear the pause flag here.
    this.isPaused = false;

    console.log('✅ Stopped public transcription', {
      segments: result.segments.length,
      commands: result.commands.length,
      durationSeconds: result.durationSeconds,
    });
    return result;
  }

  // -------- Waveform support (parallel to PublicRecordingService) --------

  private startAudioLevelDetection(): void {
    if (!this.analyser || !this.timeDomainArray) return;

    const detect = () => {
      if (!this.isRecording || this.isPaused) {
        this.audioLevelAnimationFrame = null;
        return;
      }
      if (this.analyser && this.timeDomainArray) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.analyser.getFloatTimeDomainData(this.timeDomainArray as any);
      }

      let sum = 0;
      let max = 0;
      let count = 0;
      for (let i = 0; i < this.timeDomainArray!.length; i++) {
        const sample = Math.abs(this.timeDomainArray![i]);
        max = Math.max(max, sample);
        sum += sample * sample;
        count++;
      }
      const rms = Math.sqrt(sum / count);

      let level = 0;
      if (rms > 0.0001 || max > 0.001) {
        const rmsDb = 20 * Math.log10(Math.max(0.0001, rms));
        const peakDb = 20 * Math.log10(Math.max(0.001, max));
        const rmsN = Math.max(0, Math.min(1, (rmsDb + 80) / 80));
        const peakN = Math.max(0, Math.min(1, (peakDb + 60) / 60));
        const combined = rmsN * 0.8 + peakN * 0.2;
        if (combined < 0.1) level = Math.pow(combined * 3, 0.7);
        else if (combined < 0.3) level = Math.pow(combined * 1.5, 0.8);
        else level = Math.pow(combined, 0.9);
        if (level < 0.02 && (rms > 0.0005 || max > 0.005)) {
          level = 0.02 + rms * 50;
        }
        level = Math.max(0, Math.min(1, level));
      }

      this.audioLevelListeners.forEach((cb) => cb(level));
      this.audioLevelAnimationFrame = requestAnimationFrame(detect);
    };
    this.audioLevelAnimationFrame = requestAnimationFrame(detect);
  }

  private stopAudioLevelDetection(): void {
    if (this.audioLevelAnimationFrame !== null) {
      cancelAnimationFrame(this.audioLevelAnimationFrame);
      this.audioLevelAnimationFrame = null;
    }
  }

  onAudioLevelChange(cb: AudioLevelListener): () => void {
    this.audioLevelListeners.push(cb);
    return () => {
      const i = this.audioLevelListeners.indexOf(cb);
      if (i > -1) this.audioLevelListeners.splice(i, 1);
    };
  }

  onCommandDetected(cb: CommandListener): () => void {
    this.commandListeners.push(cb);
    return () => {
      const i = this.commandListeners.indexOf(cb);
      if (i > -1) this.commandListeners.splice(i, 1);
    };
  }

  onTranscriptSegment(cb: TranscriptListener): () => void {
    this.transcriptListeners.push(cb);
    return () => {
      const i = this.transcriptListeners.indexOf(cb);
      if (i > -1) this.transcriptListeners.splice(i, 1);
    };
  }

  /**
   * Subscribe to per-section completion events. Fires whenever:
   *   - a STOP voice command closes an active section ('completed'), or
   *   - a new START voice command implicitly closes a previous one
   *     that was never STOP-ed by the clinician ('abandoned'), or
   *   - `stopRecording()` is called while a section is still open
   *     ('abandoned').
   *
   * Consumers (the page) call `aiScribeService.uploadSectionAudioUrl()`
   * from this callback to push the section's audioUrl + transcription to
   * the gateway.
   */
  onSectionCompleted(cb: SectionCompletedListener): () => void {
    this.sectionListeners.push(cb);
    return () => {
      const i = this.sectionListeners.indexOf(cb);
      if (i > -1) this.sectionListeners.splice(i, 1);
    };
  }

  /**
   * Subscribe to the one-shot "recording is live" event. Fires the
   * moment the first PCM frame is successfully sent to Deepgram —
   * confirms mic permission was granted AND the WebSocket is open
   * AND audio is flowing. The page uses this to postMessage the host
   * EHR so its Generate buttons enable only when a click would
   * actually capture data.
   *
   * Late subscribers (after the event has already fired) get
   * invoked synchronously so they don't miss the signal.
   */
  onRecordingStarted(cb: RecordingStartedListener): () => void {
    this.recordingStartedListeners.push(cb);
    if (this.hasFiredRecordingStarted) {
      try {
        cb();
      } catch (err) {
        console.warn(
          '[PublicTranscription] recordingStarted late-subscriber threw',
          err
        );
      }
    }
    return () => {
      const i = this.recordingStartedListeners.indexOf(cb);
      if (i > -1) this.recordingStartedListeners.splice(i, 1);
    };
  }

  isReadyForAudioLevels(): boolean {
    return (
      this.audioContext !== null &&
      this.analyser !== null &&
      this.timeDomainArray !== null &&
      this.isRecording &&
      !this.isPaused
    );
  }

  getRecordingState(): { isRecording: boolean; isPaused: boolean } {
    return { isRecording: this.isRecording, isPaused: this.isPaused };
  }

  /**
   * Snapshot of what's been accumulated so far. Useful for the parent window
   * if it wants to peek mid-session.
   */
  getCurrentTranscript(): TranscriptionResult {
    return {
      fullTranscript: this.transcriptSegments
        .map((s) => s.text)
        .join(' ')
        .trim(),
      segments: [...this.transcriptSegments],
      commands: [...this.detectedCommands],
      durationSeconds: Math.round(
        Math.max(0, Date.now() - this.recordingStartTime) / 1000
      ),
    };
  }

  private cleanup(): void {
    this.stopAudioLevelDetection();

    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }

    // Drop the global PCM buffer (any Generate-click in flight already
    // snapshotted the slice it needed before this cleanup ran).
    this.pcmChunks = [];

    if (this.processor) {
      try {
        this.processor.disconnect();
      } catch {
        /* noop */
      }
      this.processor.onaudioprocess = null;
      this.processor = null;
    }

    if (this.source) {
      try {
        this.source.disconnect();
      } catch {
        /* noop */
      }
      this.source = null;
    }

    if (this.analyser) {
      try {
        this.analyser.disconnect();
      } catch {
        /* noop */
      }
      this.analyser = null;
    }

    if (this.audioContext) {
      this.audioContext.close().catch(() => {
        /* noop */
      });
      this.audioContext = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }

    if (this.socket) {
      this.socket.removeEventListener('message', this.handleSocketMessage);
      this.socket.removeEventListener('close', this.handleSocketClose);
      try {
        this.socket.close(1000, 'client stop');
      } catch {
        /* noop */
      }
      this.socket = null;
    }

    this.timeDomainArray = null;
    this.audioLevelListeners = [];
    this.commandListeners = [];
    this.transcriptListeners = [];
    this.sectionListeners = [];
  }
}

// --- PCM helpers --------------------------------------------------------

function downsampleFloat32(
  input: Float32Array,
  inputRate: number,
  outputRate: number
): Float32Array {
  if (outputRate === inputRate) return input;
  if (outputRate > inputRate) throw new Error('Upsampling not supported');
  const ratio = inputRate / outputRate;
  const newLength = Math.floor(input.length / ratio);
  const result = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetInput = 0;
  while (offsetResult < newLength) {
    const nextOffsetInput = Math.floor((offsetResult + 1) * ratio);
    let accum = 0;
    let count = 0;
    for (let i = offsetInput; i < nextOffsetInput && i < input.length; i++) {
      accum += input[i];
      count++;
    }
    result[offsetResult] = count > 0 ? accum / count : 0;
    offsetResult++;
    offsetInput = nextOffsetInput;
  }
  return result;
}

function floatTo16BitPCM(float32Array: Float32Array): ArrayBuffer {
  const buffer = new ArrayBuffer(float32Array.length * 2);
  const view = new DataView(buffer);
  let offset = 0;
  for (let i = 0; i < float32Array.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
}
